import { makeAutoObservable, runInAction } from 'mobx'
import { RootStore } from '@/app/store'
import { SpacesAPI } from './api'
import { Space } from './types'
import { toast } from 'react-toastify'
import { queryClient } from '@/lib/react-query'

// Public space for all users (hardcoded)
export const PUBLIC_SPACE: Space = {
  id: 'public',
  displayName: 'Espacio Público',
  folderName: 'public',
  parentOrg: '',
  createdAt: '',
  updatedAt: '',
}

// Global BI documents space (hardcoded) - read-only for org-managers, full access for admins
export const GLOBAL_KB_SPACE: Space = {
  id: 'global-documents',
  displayName: 'Documentos BI',
  folderName: 'public',
  parentOrg: 'global',
  createdAt: '',
  updatedAt: '',
}

export class SpacesStore {
  rootStore: RootStore
  spacesAPI: SpacesAPI

  spacesById: Record<string, Space> = {}
  spaceIds: string[] = [] // To maintain order
  isLoading: boolean = false
  isCreatingSpace: boolean = false
  initiallyLoaded: boolean = false // Flag to track initial data load
  selectedSpaceId: string | null = null

  // Track loading state per space
  updatingSpacesById: Record<string, boolean> = {}
  // New state for tracking fetching individual spaces
  fetchingSpacesById: Record<string, boolean> = {}
  deletingSpacesById: Record<string, boolean> = {}

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)

    this.spacesAPI = new SpacesAPI()
  }

  public resetState() {
    this.spacesById = {}
    this.spaceIds = []
    this.isLoading = false
    this.initiallyLoaded = false
    this.selectedSpaceId = null
    this.updatingSpacesById = {}
    this.fetchingSpacesById = {}
  }

  setSelectedSpaceId(spaceId: string | null) {
    this.selectedSpaceId = spaceId
  }

  setSpaceUpdating(spaceId: string, updating: boolean) {
    this.updatingSpacesById[spaceId] = updating
  }

  isSpaceUpdating(spaceId: string): boolean {
    return !!this.updatingSpacesById[spaceId]
  }

  setSpaceFetching(spaceId: string, fetching: boolean) {
    this.fetchingSpacesById[spaceId] = fetching
  }

  isSpaceFetching(spaceId: string): boolean {
    return !!this.fetchingSpacesById[spaceId]
  }

  setSpaceDeleting(spaceId: string, deleting: boolean) {
    this.deletingSpacesById[spaceId] = deleting
  }

  isSpaceDeleting(spaceId: string): boolean {
    return !!this.deletingSpacesById[spaceId]
  }

  get selectedSpace(): Space | null {
    if (!this.selectedSpaceId) return null

    if (this.selectedSpaceId === 'public') {
      return PUBLIC_SPACE
    }

    return this.spacesById[this.selectedSpaceId] || null
  }

  // Computed property to get spaces as an array in the correct order
  get spaces(): Space[] {
    return this.spaceIds.map((id) => this.spacesById[id]).filter(Boolean)
  }

  get allSpacesWithPublic(): Space[] {
    return [GLOBAL_KB_SPACE, PUBLIC_SPACE, ...this.spaces]
  }

  async fetchSpaces() {
    const organizationId = this.rootStore.auth.user?.organization

    if (!organizationId) {
      return
    }

    // Skip if already loading
    if (this.isLoading) return

    this.isLoading = true

    try {
      const response = await this.spacesAPI.fetchSpaces(organizationId)

      runInAction(() => {
        // Clear previous data and update with new data
        this.spacesById = {}
        this.spaceIds = []

        // Store spaces by ID and maintain order
        response.docs.forEach((space) => {
          this.spacesById[space.id] = space
          this.spaceIds.push(space.id)
        })

        // Mark as initially loaded
        this.initiallyLoaded = true
      })

      return response.docs
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async getSpaceById(spaceId: string) {
    if (spaceId === 'public') {
      return PUBLIC_SPACE
    }

    try {
      const queryKey = ['space', spaceId]

      const cachedSpace = queryClient.getQueryData<Space>(queryKey)

      this.setSpaceFetching(spaceId, !cachedSpace)

      const space = await queryClient.fetchQuery<Space>({
        queryKey,
        queryFn: () => this.spacesAPI.getSpaceById(spaceId),
        staleTime: 1000 * 60 * 3, // 3 minute
      })

      runInAction(() => {
        // If the space doesn't exist in our state, add it
        this.spacesById[spaceId] = space

        // Add to spaceIds array if it's not already there
        if (!this.spaceIds.includes(spaceId)) {
          this.spaceIds.push(spaceId)
        }
      })

      return space
    } finally {
      runInAction(() => {
        this.setSpaceFetching(spaceId, false)
      })
    }
  }

  async createSpace(
    displayName: string,
    description?: string,
    departmentIds?: string[],
  ) {
    const organizationId = this.rootStore.auth.user?.organization

    if (!organizationId) {
      toast.error('Organización no encontrada')
      return
    }

    this.isCreatingSpace = true

    try {
      const { doc: space } = await this.spacesAPI.createSpace({
        parentOrg: organizationId,
        displayName,
        description,
        departments: departmentIds,
      })

      runInAction(() => {
        // Add the new space to our data structure
        this.spacesById[space.id] = space
        this.spaceIds.push(space.id)
      })

      toast.success('Espacio creado correctamente')
      return space
    } catch (error) {
      toast.error('Error creando espacio')
      throw error
    } finally {
      runInAction(() => {
        this.isCreatingSpace = false
      })
    }
  }

  async updateSpace(
    spaceId: string,
    displayName: string,
    departmentIds?: string[],
  ) {
    this.setSpaceUpdating(spaceId, true)

    try {
      const updateData: {
        displayName: string
        departments?: string[]
      } = {
        displayName,
      }

      if (departmentIds !== undefined) {
        updateData.departments = departmentIds
      }

      const updatedSpace = await this.spacesAPI.updateSpace(spaceId, updateData)

      await this.getSpaceById(spaceId)

      toast.success('Espacio actualizado correctamente')
      return updatedSpace
    } catch (error) {
      toast.error('Error actualizando espacio')
      throw error
    } finally {
      runInAction(() => {
        this.setSpaceUpdating(spaceId, false)
      })
    }
  }

  async deleteSpace(spaceId: string) {
    this.setSpaceDeleting(spaceId, true)

    try {
      await this.spacesAPI.deleteSpace(spaceId)

      runInAction(() => {
        // Remove from our map
        delete this.spacesById[spaceId]

        // Remove from the order array
        this.spaceIds = this.spaceIds.filter((id) => id !== spaceId)

        // Reset selected space if it was deleted
        if (this.selectedSpaceId === spaceId) {
          this.selectedSpaceId = 'public'
        }
      })

      toast.success('Espacio eliminado correctamente')
    } catch (error) {
      toast.error('Error eliminando espacio')
      throw error
    } finally {
      runInAction(() => {
        this.setSpaceDeleting(spaceId, false)
      })
    }
  }

  async updateSpaceDepartments(spaceId: string, departmentIds: string[]) {
    this.setSpaceUpdating(spaceId, true)

    try {
      // Update space with new department assignments
      const updatedSpace = await this.spacesAPI.updateSpaceDepartments(
        spaceId,
        departmentIds,
      )

      // Update the space in our local state
      runInAction(() => {
        this.spacesById[spaceId] = updatedSpace.doc
      })

      toast.success('Departamentos del espacio actualizados correctamente')
      return updatedSpace
    } catch (error) {
      toast.error('Error actualizando departamentos del espacio')
      throw error
    } finally {
      runInAction(() => {
        this.setSpaceUpdating(spaceId, false)
      })
    }
  }
}
